<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import _ from 'lodash'
import { ref, onMounted, watch, computed, getCurrentInstance } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import FormButton from '../Atoms/FormButton.vue'
import StreamService from '@/services/StreamService'
import FormInput from '../Atoms/FormInput.vue'
import FormText from '../Atoms/FormText.vue'
import Alert from '../Atoms/Alert.vue'
import FormUpload from '../Atoms/FormUpload.vue'
import AlertModal from '@/components/modals/AlertModal.vue'
import FormSwitch from '../Atoms/FormSwitch.vue'
import Tooltip from '../Atoms/Tooltip.vue'
import PaywallService from '@/services/PaywallService'
import IconBase from '@/components/icon/IconBase.vue';
import IconInfo from '@/components/icon/IconInfo.vue';
import { applyMediaToQuery, parseMediaQuery } from '@/Utils/vodVideoManageQuery'

const { notify } = useNotification()
const store = useStore()
const instance = getCurrentInstance();
const root = instance.proxy.$root;
const route = useRoute()
const router = useRouter()

const props = defineProps({
  vodFile: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id');
    }
  },
  vodFolder: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id');
    }
  },
  playerUrl: {
    type: String
  },
  streamMeta: {
    type: Object,
  },
  mediaOnly: {
    type: Boolean,
    default: false,
  }
})

// ─── Non-mediaOnly state ───────────────────────────────────────────────────
const title = ref('')
const description = ref('')
const videoMeta = ref(null)

const isLoading = ref({
  title: false,
  description: false,
})

// ─── Poster / End poster state ────────────────────────────────────────────
const embedPosterTemp = ref(null)
const endEmbedPosterTemp = ref(null)

const embedPoster = ref({
  error: false,
  enabled: false,
  value: null,
  valueType: 'string'
})
const endEmbedPoster = ref({
  error: false,
  enabled: false,
  value: null,
  valueType: 'string'
})

// ─── Animated poster state (new) ──────────────────────────────────────────
const animatedPoster = ref({
  error: false,
  enabled: false,
  value: null,
  valueType: 'string'
})
const animatedPosterTemp = ref(null)
const animatedPreview = ref(false)

// Holds the actual File picked for each poster type. The animated-poster input
// is remounted when a preview is set (the v-if/v-else on animatedPosterUrl swaps
// the FormUpload), which discards the file from the DOM input — so we keep the
// File here and read from it on save rather than re-querying the input.
const pendingPosterFiles = ref({ embedPoster: null, animatedPoster: null, endEmbedPoster: null })
const posterFileKey = (type) =>
  type === 'endEmbedPoster' ? 'endEmbedPoster'
  : type === 'animatedPoster' ? 'animatedPoster'
  : 'embedPoster'

// ─── Tab state ────────────────────────────────────────────────────────────
/** @type {'poster' | 'animated' | 'end'} */
const activeAsset = ref('poster')

const setActiveAsset = (asset) => {
  activeAsset.value = asset
  if (!props.mediaOnly) return
  router.replace({ query: applyMediaToQuery(route.query, asset) })
}

watch(
  () => route.query.media,
  () => {
    if (!props.mediaOnly) return
    activeAsset.value = parseMediaQuery(route.query.media)
  },
  { immediate: true },
)

const maxPosterUploadMB = ref(null)
const mimeErrorMessage = ref('Only PNG/JPG/GIF/AVIF image files are allowed')

// ─── Computed URLs ────────────────────────────────────────────────────────
const posterUrl = computed(() => {
  if (embedPosterTemp.value) return embedPosterTemp.value
  const imageId = embedPoster.value.value;
  return imageId ? `https://assets.castr.io/embedImages/${imageId}` : null;
})

const endPosterUrl = computed(() => {
  if (endEmbedPosterTemp.value) return endEmbedPosterTemp.value
  const imageId = endEmbedPoster.value.value;
  return imageId ? `https://assets.castr.io/embedImages/${imageId}` : null;
})

const animatedPosterUrl = computed(() => {
  if (animatedPosterTemp.value) return animatedPosterTemp.value
  const imageId = animatedPoster.value.value;
  return imageId ? `https://assets.castr.io/embedImages/${imageId}` : null;
})

// ─── Auto-generated poster candidates ─────────────────────────────────────
// The transcoder writes candidate frames to the CDN at a predictable path:
//   vod_uploads/<folderKey>/<videoId>-poster_1.jpg … -poster_6.jpg
// We show whichever exist; picking one uploads it through the normal poster
// endpoint so it becomes the video's poster exactly like a manual upload.
const posterCandidateBase = computed(() => {
  const key = props.vodFolder?.key
  const vid = String(props.vodFile?.id || '').replace(/\.mp4$/i, '')
  if (!key || !vid) return null
  return `https://vod-assets.castr.net/vod_uploads/${key}/${vid}`
})
const posterCandidates = computed(() => {
  if (!posterCandidateBase.value) return []
  return Array.from({ length: 6 }, (_, i) => ({ n: i + 1, url: `${posterCandidateBase.value}-poster_${i + 1}.jpg` }))
})
// Only reveal a candidate once its image actually loads (avoids broken tiles).
// Probe from script rather than an <img @load> — a cached/complete image can
// fire load before the template listener attaches, leaving the tile hidden.
const okCandidate = ref({})
const hasCandidates = computed(() => Object.values(okCandidate.value).some(Boolean))
const applyingCandidate = ref(null)

watch(posterCandidates, (list) => {
  okCandidate.value = {}
  list.forEach((c) => {
    const im = new Image()
    im.onload = () => { okCandidate.value[c.url] = true }
    im.src = c.url
  })
}, { immediate: true })

const selectPosterCandidate = async (url) => {
  if (applyingCandidate.value) return
  applyingCandidate.value = url
  try {
    const resp = await fetch(url, { mode: 'cors' })
    if (!resp.ok) throw new Error('fetch failed')
    const blob = await resp.blob()
    const fdata = new FormData()
    fdata.append('file', new File([blob], 'poster.jpg', { type: blob.type || 'image/jpeg' }))
    // isWatermark=false → saved as embedPoster server-side (same as manual upload)
    const res = await StreamService.uploadStreamPoster(props.vodFolder._id, fdata, false, props.vodFile._id, false)
    if (!res.success) throw new Error('upload failed')
    embedPoster.value.value = res.uploadId
    embedPosterTemp.value = null
    const poster = `https://assets.castr.io/embedImages/${res.uploadId}`
    PaywallService.updateStreamPaywall(props.vodFolder._id, { wallpaper_url: poster }, props.vodFile.id).catch((err) => {
      console.error('update paywall stream poster failed: ', err)
    })
    notify({ group: 'success', text: 'Poster updated' })
  } catch (e) {
    notify({ group: 'error', text: 'Could not apply this poster. Try uploading it instead.' })
  }
  applyingCandidate.value = null
}

// ─── Lifecycle ────────────────────────────────────────────────────────────
onMounted(async () => {
  getMeta()
})

watch([() => `${route.params.streamId}/${route.params.videoId}`, () => props.streamMeta], async () => {
  getMeta()
});

const getMeta = async () => {
  //TODO Move this call in parent component to prevent double Meta call for components (Chapters, Schedule, etc.)
  videoMeta.value = props.streamMeta?.videoMeta
  if (props.streamMeta?.videoMeta?.title) {
    title.value = props.streamMeta.videoMeta?.title;
  }

  if (props.streamMeta?.videoMeta?.description) {
    description.value = props.streamMeta.videoMeta?.description;
  }
  if (props.streamMeta?.videoMeta?.embedPoster) embedPoster.value.value = props.streamMeta.videoMeta.embedPoster
  if (props.streamMeta?.videoMeta?.endEmbedPoster) endEmbedPoster.value.value = props.streamMeta.videoMeta.endEmbedPoster
  // Hydrate animated poster state
  if (props.streamMeta?.videoMeta?.animatedPoster) {
    animatedPoster.value.value = props.streamMeta.videoMeta.animatedPoster
  }
  if (props.streamMeta?.videoMeta?.animatedPreview !== undefined) {
    animatedPreview.value = Boolean(props.streamMeta.videoMeta.animatedPreview)
  }
}

// ─── Non-mediaOnly handlers ───────────────────────────────────────────────
const onChangeTitle = async () => {
  isLoading.title = true;
  if (title.value.length > 100) {
    title.value = title.value.substring(0, 97) + '...';
  }
  await saveSetting('title', title.value, false, props.vodFile._id);
  videoMeta.value.title = title.value
  isLoading.title = false;
}

const onChangeDescription = async () => {
  isLoading.description = true;
  if (description.value.length > 1000) {
    description.value = description.value.substring(0, 997) + '...';
  }
  await saveSetting('description', description.value, false, props.vodFile._id);
  videoMeta.value.description = description.value
  isLoading.description = false;
}

// ─── Shared helpers ───────────────────────────────────────────────────────
const saveSetting = async (key, value, successToast = false, videoId) => {
  isLoading.value[key] = true;
  try {
    await StreamService.saveStreamMetadata(route.params.streamId, key, value, videoId);
    // clear stream meta cache in embed player
    StreamService.clearStreamMetadataCache(route.params.streamId);
    if (successToast) {
      notify({ group: 'success', text: 'Setting saved' })
    }
  } catch (e) {
    notify({ group: 'error', text: 'Unable to save your changes.' });
  }
  isLoading.value[key] = false;
}

function imageReader(file, cb) {
  if (!file) return;
  var reader = new FileReader();
  reader.onload = function () {
    if (cb) cb(reader.result);
  };
  reader.readAsDataURL(file);
}

// ─── Image preview + upload handlers ─────────────────────────────────────
const onEmbedPosterPreview = (file, type) => {
  let selector;
  if (type === 'endEmbedPoster') selector = '#end-embed-poster-input';
  else if (type === 'animatedPoster') selector = '#animated-poster-input';
  else selector = '#embed-poster-input';

  const imageInput = document.querySelector(selector);
  const imageFile = file;
  if (!imageFile) return;

  if (imageFile.type.indexOf('image/') === -1) {
    showModal('embed-upload-format');
    imageInput.value = null;
    return;
  }

  // Animated poster allows up to 4 MB; static (and end) posters up to 2 MB.
  let maxSize = type === 'animatedPoster' ? 4 : 2;
  maxPosterUploadMB.value = maxSize;
  maxSize *= 1024 * 1024;

  if (imageFile.size > maxSize) {
    showModal('vod-poster-size-exceed');
    imageInput.value = null;
    return;
  }

  const animatedValidTypes = ['webp', 'gif', 'avif', 'apng']
  const standardValidTypes = ['png', 'jpeg', 'gif', 'avif']
  const uploadedImageFileType = imageFile.type.replace('image/', '');

  const validTypes = type === 'animatedPoster' ? animatedValidTypes : standardValidTypes;
  if (validTypes.indexOf(uploadedImageFileType) === -1) {
    // Message reflects the formats valid for THIS poster type (animated posters
    // accept WebP/AVIF/APNG/GIF, static posters PNG/JPG/GIF/AVIF).
    mimeErrorMessage.value = type === 'animatedPoster'
      ? 'Only WebP/AVIF/APNG/GIF image files are allowed'
      : 'Only PNG/JPG/GIF/AVIF image files are allowed';
    showModal('vod-poster-mime-type');
    imageInput.value = null;
    return;
  }

  // Retain the File so save doesn't depend on the (possibly remounted) input.
  pendingPosterFiles.value[posterFileKey(type)] = imageFile;

  imageReader(imageFile, base64 => {
    if (type === 'endEmbedPoster') {
      endEmbedPosterTemp.value = base64;
    } else if (type === 'animatedPoster') {
      animatedPosterTemp.value = base64;
    } else {
      embedPosterTemp.value = base64;
    }
  });
}

const saveEmbedPoster = async (type) => {
  let featureProp;
  if (type === 'endEmbedPoster') featureProp = 'endEmbedPoster';
  else if (type === 'animatedPoster') featureProp = 'animatedPoster';
  else featureProp = 'embedPoster';

  // Clear error state on the relevant ref
  if (featureProp === 'endEmbedPoster') endEmbedPoster.value.error = false;
  else if (featureProp === 'animatedPoster') animatedPoster.value.error = false;
  else embedPoster.value.error = false;

  let selector;
  if (featureProp === 'endEmbedPoster') selector = '#end-embed-poster-input';
  else if (featureProp === 'animatedPoster') selector = '#animated-poster-input';
  else selector = '#embed-poster-input';

  const imageInput = document.querySelector(selector);
  // Prefer the retained File (the animated-poster input gets remounted after
  // preview, losing its files); fall back to the live input.
  const imageFile = pendingPosterFiles.value[featureProp] || imageInput?.files?.[0];

  if (!imageFile) {
    const errMsg = 'please pick an image file';
    if (featureProp === 'endEmbedPoster') endEmbedPoster.value.error = errMsg;
    else if (featureProp === 'animatedPoster') animatedPoster.value.error = errMsg;
    else embedPoster.value.error = errMsg;
    return;
  }

  isLoading.value[featureProp] = true;
  console.log({ type });

  const fdata = new FormData();
  fdata.append('file', imageFile);

  const isSecondary = type === 'endEmbedPoster';

  const res = await StreamService.uploadStreamPoster(
    props.vodFolder._id,
    fdata,
    isSecondary,
    props.vodFile._id,
    false
  );

  if (res.success) {
    if (featureProp === 'endEmbedPoster') {
      endEmbedPosterTemp.value = null;
    } else if (featureProp === 'animatedPoster') {
      animatedPosterTemp.value = null;
      animatedPoster.value.value = res.uploadId;
      await saveSetting('animatedPoster', res.uploadId, false, props.vodFile._id);
    } else {
      const poster = `https://assets.castr.io/embedImages/${res.uploadId}`;
      PaywallService.updateStreamPaywall(props.vodFolder._id, { wallpaper_url: poster }, props.vodFile.id).catch(err => {
        console.error('update paywall stream poster failed: ', err)
      });
      embedPosterTemp.value = null;
    }

    if (featureProp !== 'animatedPoster') {
      if (type === 'endEmbedPoster') endEmbedPoster.value.value = res.uploadId;
      else embedPoster.value.value = res.uploadId;
    }

    const label = featureProp === 'endEmbedPoster' ? 'End Embed'
      : featureProp === 'animatedPoster' ? 'Animated Poster'
      : 'Poster';
    pendingPosterFiles.value[featureProp] = null;
    notify({ group: 'success', text: `${label} uploaded successfully.` });
  } else {
    const errMsg = 'Unable to upload the image. Please try again later.';
    if (featureProp === 'endEmbedPoster') endEmbedPoster.value.error = errMsg;
    else if (featureProp === 'animatedPoster') animatedPoster.value.error = errMsg;
    else embedPoster.value.error = errMsg;
  }

  isLoading.value[featureProp] = false;
}

const cancelUpload = async (type) => {
  let selector;
  if (type === 'endEmbedPoster') selector = '#end-embed-poster-input';
  else if (type === 'animatedPoster') selector = '#animated-poster-input';
  else selector = '#embed-poster-input';

  if (type === 'endEmbedPoster') {
    endEmbedPosterTemp.value = null;
    endEmbedPoster.value.error = false;
    endEmbedPoster.value.value = null;
  } else if (type === 'animatedPoster') {
    animatedPosterTemp.value = null;
    animatedPoster.value.error = false;
    animatedPoster.value.value = null;
  } else {
    embedPosterTemp.value = null;
    embedPoster.value.error = false;
    embedPoster.value.value = null;
  }

  pendingPosterFiles.value[posterFileKey(type)] = null;

  const el = document.querySelector(selector);
  if (el) el.value = null;

  removePoster(type);
}

const removePoster = async (type) => {
  try {
    const settingsProp =
      type === 'endEmbedPoster' ? 'endEmbedPoster'
      : type === 'animatedPoster' ? 'animatedPoster'
      : 'embedPoster';
    await saveSetting(settingsProp, null, true, props.vodFile._id);
  } catch (e) {
    console.log(e);
  }
}

// ─── Animated preview toggle ──────────────────────────────────────────────
const onAnimatedPreviewChange = (value) => {
  saveSetting('animatedPreview', value);
}
</script>

<template>
  <section>
    <!-- ── Non-mediaOnly: title + description ─────────────────────────────── -->
    <template v-if="!mediaOnly">
      <div class="flex flex-col gap-y-3 items-start !mt-4">
        <FormInput label="Title" v-model="title" :maxlength="100" />
        <FormButton :label="isLoading.title ? 'Saving ..' : 'Save'"
          :disabled="isLoading.title || title === (videoMeta?.title || '')" @click="onChangeTitle" />
        <label for="description" class="mb-0 text-sm">Description</label>
        <FormText id="description" custom-classes="!h-20" v-model="description" />
        <FormButton :label="isLoading.description ? 'Saving ..' : 'Save'"
          :disabled="isLoading.description || description === (videoMeta?.description || '')"
          @click="onChangeDescription" />
      </div>
      <hr class="border-surface-3 !my-4">
    </template>

    <!-- ── mediaOnly: tabbed Media Assets card ───────────────────────────── -->
    <template v-if="mediaOnly">
      <!-- Segmented tab bar — compact, sized to content (not full width) -->
      <div class="inline-flex items-center gap-1 bg-surface-3 rounded-md p-1">
        <FormButton
          :custom-classes="'text-sm font-medium rounded-md py-1.5 px-3 transition-colors ' + (activeAsset === 'poster' ? 'bg-surface-4 text-white shadow-sm' : 'text-surface-8 hover:text-white')"
          @click="setActiveAsset('poster')"
        >
          Poster
        </FormButton>
        <FormButton
          :custom-classes="'text-sm font-medium rounded-md py-1.5 px-3 transition-colors ' + (activeAsset === 'animated' ? 'bg-surface-4 text-white shadow-sm' : 'text-surface-8 hover:text-white')"
          @click="setActiveAsset('animated')"
        >
          Animated
        </FormButton>
        <FormButton
          :custom-classes="'text-sm font-medium rounded-md py-1.5 px-3 transition-colors ' + (activeAsset === 'end' ? 'bg-surface-4 text-white shadow-sm' : 'text-surface-8 hover:text-white')"
          @click="setActiveAsset('end')"
        >
          End poster
        </FormButton>
      </div>

      <!-- Tab content -->
      <div class="mt-3 w-full">

        <!-- ── Poster tab ─────────────────────────────────────────────────── -->
        <div v-if="activeAsset === 'poster'" class="flex flex-col gap-y-3 items-start w-full">
          <!-- Auto-generated poster candidates -->
          <div v-show="hasCandidates" class="w-full">
            <p class="text-tiny text-surface-7 mb-2">Choose from generated posters</p>
            <div class="grid grid-cols-3 gap-2">
              <FormButton v-for="c in posterCandidates" :key="c.n" v-show="okCandidate[c.url]"
                custom-classes="group relative aspect-video rounded-md overflow-hidden ring-1 ring-surface-3 hover:ring-main transition"
                :disabled="!!applyingCandidate" @click="selectPosterCandidate(c.url)">
                <img :src="c.url" alt="" class="w-full h-full object-cover" />
                <span class="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors"></span>
                <span v-if="applyingCandidate === c.url"
                  class="absolute inset-0 grid place-items-center bg-black/45">
                  <span class="inline-block size-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                </span>
              </FormButton>
            </div>
          </div>

          <img v-if="posterUrl" :src="posterUrl" alt="Poster preview" class="size-[100px] rounded object-cover" />
          <p v-else class="text-tiny text-surface-6">No poster set</p>
          <div class="flex flex-wrap gap-2 items-center w-full">
            <FormUpload
              name="poster"
              id="embed-poster-input"
              placeholder="No file chosen"
              :label="embedPoster.value ? 'Replace poster' : 'Upload poster'"
              type="secondary"
              size="sm"
              show-icon
              :on-file-change="onEmbedPosterPreview"
            />
            <FormButton
              v-if="embedPosterTemp"
              :label="isLoading.embedPoster ? 'Saving' : 'Save'"
              :disabled="isLoading.embedPoster"
              size="sm"
              @click="saveEmbedPoster()"
            />
            <FormButton
              v-if="embedPosterTemp || embedPoster.value"
              :disabled="isLoading.embedPoster"
              type="secondary"
              size="sm"
              @click="cancelUpload()"
            >
              <span v-if="embedPosterTemp && embedPoster.value">Restore</span>
              <span v-else-if="embedPosterTemp">Cancel</span>
              <span v-else>Remove</span>
            </FormButton>
            <span class="ml-auto text-tiny text-surface-7">1280×720 recommended</span>
          </div>
          <Alert type="error" v-show="embedPoster.error" :text="embedPoster.error || ''" />
        </div>

        <!-- ── Animated Poster tab ────────────────────────────────────────── -->
        <div v-else-if="activeAsset === 'animated'" class="flex flex-col items-start w-full">
          <p class="text-xs text-surface-7">File types: WebP (recommended), AVIF, APNG, GIF</p>
          <p class="text-xs text-surface-7 mb-3">Size: 4MB max</p>

          <!-- Animated Preview toggle — only relevant once a file is uploaded -->
          <div v-if="animatedPosterUrl" class="flex items-center gap-2">
            <FormSwitch
              id="animated-preview-switch"
              size="sm"
              label="Animated Preview"
              v-model="animatedPreview"
              @change="onAnimatedPreviewChange(animatedPreview)"
            />
            <Tooltip content="Show an animated preview when hovering over the video thumbnail">
              <icon-base class="text-surface-6 cursor-help" width="14" height="14">
                <icon-info />
              </icon-base>
            </Tooltip>
          </div>

          <!-- If animated poster already set -->
          <template v-if="animatedPosterUrl">
            <img :src="animatedPosterUrl" alt="Animated poster preview" class="size-[100px] rounded object-cover" />
            <div class="flex flex-wrap gap-2 items-center w-full">
              <FormUpload
                name="animatedPoster"
                id="animated-poster-input"
                placeholder="No file chosen"
                label="Replace animated poster"
                type="secondary"
                size="sm"
                accept="image/webp,image/gif,image/avif,image/apng"
                :on-file-change="(file) => onEmbedPosterPreview(file, 'animatedPoster')"
              />
              <FormButton
                v-if="animatedPosterTemp"
                :label="isLoading.animatedPoster ? 'Saving' : 'Save'"
                :disabled="isLoading.animatedPoster"
                size="sm"
                @click="saveEmbedPoster('animatedPoster')"
              />
              <FormButton
                :disabled="isLoading.animatedPoster"
                type="secondary"
                size="sm"
                @click="cancelUpload('animatedPoster')"
              >
                <span v-if="animatedPosterTemp && animatedPoster.value">Restore</span>
                <span v-else-if="animatedPosterTemp">Cancel</span>
                <span v-else>Remove</span>
              </FormButton>
            </div>
          </template>

          <!-- No animated poster yet -->
          <template v-else>
            <div class="flex flex-wrap gap-2 items-center w-full">
              <FormUpload
                name="animatedPoster"
                id="animated-poster-input"
                placeholder="No file chosen"
                label="Upload animated poster"
                type="main"
                size="sm"
                accept="image/webp,image/gif,image/avif,image/apng"
                :on-file-change="(file) => onEmbedPosterPreview(file, 'animatedPoster')"
              />
              <FormButton
                v-if="animatedPosterTemp"
                :label="isLoading.animatedPoster ? 'Saving' : 'Save'"
                :disabled="isLoading.animatedPoster"
                size="sm"
                @click="saveEmbedPoster('animatedPoster')"
              />
            </div>
          </template>

          <Alert type="error" v-show="animatedPoster.error" :text="animatedPoster.error || ''" />
        </div>

        <!-- ── End poster tab ─────────────────────────────────────────────── -->
        <div v-else-if="activeAsset === 'end'" class="flex flex-col gap-y-3 items-start w-full">
          <img v-if="endPosterUrl" :src="endPosterUrl" alt="End poster preview" class="size-[100px] rounded object-cover" />
          <p v-else class="text-tiny text-surface-6">No end poster set</p>
          <div class="flex flex-wrap gap-2 items-center w-full">
            <FormUpload
              name="endEmbedPoster"
              id="end-embed-poster-input"
              placeholder="No file chosen"
              :label="endEmbedPoster.value ? 'Replace end poster' : 'Upload end poster'"
              type="secondary"
              size="sm"
              show-icon
              :on-file-change="(file) => onEmbedPosterPreview(file, 'endEmbedPoster')"
            />
            <FormButton
              v-if="endEmbedPosterTemp"
              :label="isLoading.endEmbedPoster ? 'Saving' : 'Save'"
              :disabled="isLoading.endEmbedPoster"
              size="sm"
              @click="saveEmbedPoster('endEmbedPoster')"
            />
            <FormButton
              v-if="endEmbedPosterTemp || endEmbedPoster.value"
              :disabled="isLoading.endEmbedPoster"
              type="secondary"
              size="sm"
              @click="cancelUpload('endEmbedPoster')"
            >
              <span v-if="endEmbedPosterTemp && endEmbedPoster.value">Restore</span>
              <span v-else-if="endEmbedPosterTemp">Cancel</span>
              <span v-else>Remove</span>
            </FormButton>
            <span class="ml-auto text-tiny text-surface-7">1280×720 recommended</span>
          </div>
          <Alert type="error" v-show="endEmbedPoster.error" :text="endEmbedPoster.error || ''" />
        </div>

      </div>
      <!-- /tab content -->

    </template>

    <!-- ── Non-mediaOnly: Thumbnail + End Embed Poster ──────────────────────── -->
    <template v-if="!mediaOnly">
      <div class="flex flex-col gap-y-3 items-start w-full">
        <label for="embed-poster-input" class="mb-0 text-sm">Thumbnail</label>
        <div class="flex flex-wrap gap-2 items-center w-full">
          <img v-if="posterUrl" :src="posterUrl" alt="thumbnail image" class="size-[100px]">
          <p v-else class="text-tiny text-surface-6">No file chosen</p>
          <FormUpload class="ml-auto" name="poster" id="embed-poster-input" placeholder="No file chosen"
            :label="embedPoster.value ? 'Change' : 'Select Thumbnail'" type="secondary" size="sm"
            :on-file-change="onEmbedPosterPreview" />
          <FormButton v-if="embedPosterTemp" :label="isLoading.embedPoster ? 'Saving' : 'Save Thumbnail'"
            :disabled="isLoading.embedPoster" size="sm" @click="saveEmbedPoster" />
          <FormButton v-if="embedPosterTemp || embedPoster.value" :disabled="isLoading.embedPoster" type="secondary"
            size="sm" @click="cancelUpload">
            <span v-if="embedPosterTemp && embedPoster.value">Restore</span>
            <span v-else-if="embedPosterTemp">Cancel</span>
            <span v-else>Remove</span>
          </FormButton>
        </div>
        <Alert type="error" v-show="embedPoster.error" :text="embedPoster.error || ''" />
      </div>
      <hr class="border-surface-3 !my-4">
      <div class="flex flex-col gap-y-3 items-start w-full">
        <label for="embed-poster-input" class="mb-0 text-sm">End Embed Poster</label>
        <div class="flex flex-wrap gap-2 items-center w-full">
          <img v-if="endPosterUrl" :src="endPosterUrl" alt="thumbnail image" class="size-[100px]">
          <p v-else class="text-tiny text-surface-6">No file chosen</p>
          <FormUpload class="ml-auto" name="endEmbedPoster" id="end-embed-poster-input" placeholder="No file chosen"
            :label="endEmbedPoster.value ? 'Change' : 'Select Poster'" type="secondary" size="sm"
            :on-file-change="onEmbedPosterPreview" />
          <FormButton v-if="endEmbedPosterTemp" :label="isLoading.endEmbedPoster ? 'Saving' : 'Save Poster'"
            :disabled="isLoading.endEmbedPoster" size="sm" @click="saveEmbedPoster('endEmbedPoster')" />
          <FormButton v-if="endEmbedPosterTemp || endEmbedPoster.value" :disabled="isLoading.endEmbedPoster"
            type="secondary" size="sm" @click="cancelUpload('endEmbedPoster')">
            <span v-if="endEmbedPosterTemp && endEmbedPoster.value">Restore</span>
            <span v-else-if="endEmbedPosterTemp">Cancel</span>
            <span v-else>Remove</span>
          </FormButton>
        </div>
        <Alert type="error" v-show="endEmbedPoster.error" :text="endEmbedPoster.error || ''" />
      </div>
    </template>
  </section>

  <!-- Local size-exceed alert so the message reflects the actual per-type limit
       (the shared modal in VODStreamManage is hardcoded to 2MB). -->
  <AlertModal modal-id="vod-poster-size-exceed"
    :message="'Uploaded image too large. Image must not exceed ' + maxPosterUploadMB + 'MB'" />
  <AlertModal modal-id="vod-poster-mime-type" :message="mimeErrorMessage" />
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped></style>
